// Client utility for spec sheet processing.
// All Anthropic API calls happen server-side in the extract-specs edge function.

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

export interface ConsumerSpecs {
  dimensions: { height: string | null; width: string | null; depth: string | null } | null;
  weight: string | null;
  btu_output: string | null;
  fuel_type: string | null;
  materials: string[] | null;
  finish_options: string[] | null;
  warranty: string | null;
  certifications: string[] | null;
  colors_available: string[] | null;
  key_features: string[] | null;
}

export interface InstallerSpecs {
  clearance_requirements: string | null;
  venting_type: string | null;
  gas_line_specs: string | null;
  electrical_requirements: string | null;
  framing_requirements: string | null;
  installation_notes: string[] | null;
  required_accessories: string[] | null;
  estimated_install_time: string | null;
}

/** A value exactly as the manufacturer printed it (migration 20261014000000). */
export interface ClearanceOriginal {
  value: number;
  unit: "in" | "mm";
}

export type ClearanceDirectionKey = "rear_mm" | "side_mm" | "front_mm" | "ceiling_mm" | "floor_mm";

/**
 * Structured clearances for the AR overlay. The *_mm fields are integer mm
 * (authoritative for compatibility); `original` keeps the manufacturer's
 * printed value + unit per clearance field — the mobile app checks against
 * the original's exact mm and shows it verbatim when units match.
 */
export interface Clearances {
  rear_mm: number | null;
  side_mm: number | null;
  front_mm: number | null;
  ceiling_mm: number | null;
  floor_mm: number | null;
  /** Overall product width in mm — extracted from spec sheet; undefined on legacy records */
  width_mm?: number | null;
  /** Overall product depth (front-to-back) in mm — extracted from spec sheet; undefined on legacy records */
  depth_mm?: number | null;
  /** Overall product height (floor-to-top) in mm — extracted from spec sheet; undefined on legacy records */
  height_mm?: number | null;
  /** Manufacturer's printed value + unit per clearance field; absent on legacy records */
  original?: Partial<Record<ClearanceDirectionKey | "floor_protection_radius_mm" | "mantel_height_mm" | "mantel_depth_mm" | "hearth_extension_mm", ClearanceOriginal>>;
}

/** Reads a well-formed original from any clearances-like object; null when absent or malformed. */
export function clearanceOriginal(cl: unknown, key: string): ClearanceOriginal | null {
  if (!cl || typeof cl !== "object") return null;
  const orig = (cl as { original?: unknown }).original;
  if (!orig || typeof orig !== "object") return null;
  const o = (orig as Record<string, unknown>)[key] as { value?: unknown; unit?: unknown } | undefined;
  if (!o || typeof o.value !== "number" || !(o.value > 0) || (o.unit !== "in" && o.unit !== "mm")) return null;
  return { value: o.value, unit: o.unit };
}

/** "16 in" / "150 mm" / "1.5 in" — the printed value, never rounded. */
export function formatClearanceOriginal(o: ClearanceOriginal): string {
  return `${Number(o.value.toFixed(4))} ${o.unit}`;
}

export interface ExtractedSpecs {
  consumerSpecs: ConsumerSpecs;
  installerSpecs: InstallerSpecs;
  clearances?: Clearances | null;
  partial?: boolean;
  extractionWarning?: string;
}

export async function extractSpecSheet(
  productName: string,
  category: string,
  jwt: string,
  consumerPdfUrl?: string,
  installPdfUrl?: string,
): Promise<ExtractedSpecs> {
  if (!consumerPdfUrl && !installPdfUrl) {
    throw new Error("At least one PDF URL (consumer or install) is required");
  }
  const url = `${SUPABASE_URL}/functions/v1/extract-specs`;
  console.log("Calling URL:", url);
  console.log("Method: POST");
  console.log("Auth token present:", !!jwt);
  const preflight = await fetch(url, { method: "OPTIONS" });
  console.log("OPTIONS status:", preflight.status);
  console.log("OPTIONS headers:", Object.fromEntries(preflight.headers.entries()));
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwt}`,
      apikey: ANON_KEY,
    },
    body: JSON.stringify({
      consumer_pdf_url: consumerPdfUrl ?? null,
      install_pdf_url: installPdfUrl ?? null,
      product_name: productName,
      category,
    }),
  });

  const body = await res.text();
  if (!res.ok) {
    throw new Error(`Spec extraction failed (${res.status}): ${body}`);
  }

  const { consumer_specs, installer_specs, clearances, partial, extraction_warning } = JSON.parse(body);
  return {
    consumerSpecs: consumer_specs,
    installerSpecs: installer_specs,
    clearances: clearances ?? null,
    partial: partial ?? false,
    extractionWarning: extraction_warning,
  };
}

export async function getSpecUploadUrl(
  productId: string,
  filename: string,
  jwt: string
): Promise<{ uploadUrl: string; publicUrl: string }> {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/get-spec-upload-url`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwt}`,
      apikey: ANON_KEY,
    },
    body: JSON.stringify({ product_id: productId, filename }),
  });

  const body = await res.text();
  if (!res.ok) {
    throw new Error(`Failed to get upload URL (${res.status}): ${body}`);
  }

  const { upload_url, public_url } = JSON.parse(body);
  return { uploadUrl: upload_url, publicUrl: public_url };
}
